<script>
  import { h } from 'vue';
  import { getMediaObject } from '@services/vueMediaSvc';
  import MrIcon from '@components/MrIcon';

  export default {
    name: 'ImgBox',

    components: {
      MrIcon,
    },

    inheritAttrs: false,

    props: {
      default: () => {},
      mediaId: [String, Number],
      mediaObj: Object,
      loading: {
        type: String,
        required: false,
        default: 'lazy'
      },
      quality: {
        type: Number
      }
    },

    render() {
      if (this.isSvg) {

        if (!this.svgContent) {
          // eslint-disable-next-line no-console
          console.error('No svg content found, selected SVG is not supported');
          return null;
        }
        const props = {
          content: this.svgContent,
        };
        props.style = { ...this.$attrs.style || {} };
        let heightAttr = this.$attrs.height;
        let colorAttr = this.$attrs.color;

        if (this.isNewSvg) {
          props.original = this.mediaObject.originalColor;
          props.alt = this.mediaObject.icon?.alt_text || '';
          if (this.mediaObject.originalColor) {
            props.style.stroke = 'unset';
          } else {
            props.fill = this.mediaObject.fill;
            props.style.color = `${colorAttr || this.mediaObject.color}`;
          }
          if (this.mediaObject.size) {
            props.height = `${this.mediaObject.size}em`;
            heightAttr = props.height;
          }
        } else {
          props.alt = this.mediaObject?.alt_text || '';
        }

        return h(MrIcon, {
          ...props,
          class: 'icon-box' + (!heightAttr ? ' default-size' : '') + (this.$attrs.class || ''),
        });
      }

      const imageNode = this.src
        ? h('img', {
          ...this.$attrs,
          src: this.src,
          alt: this.mediaObject?.alt_text || '',
          loading: this.loading,
          'data-test': 'img-box',
        })
        : null;

      return h('div', {
        class: 'image-box ' + (this.$attrs.class || ''),
        ref: 'box',
      }, [imageNode]);
    },

    data() {
      return {
        width: 0,
        height: 0,
        interval: null,
        asyncMediaObj: null
      };
    },

    computed: {
      isNewSvg() {
        return !!(this.mediaObject && this.mediaObject?.icon && this.mediaObject.icon?.file_type === 'svg+xml' && this.mediaObject.icon?.svg_data);
      },

      isLegacySvg() {
        return !!(this.mediaObject?.file_type === 'svg+xml' && this.mediaObject?.svg_data && !this.isNewSvg);
      },

      isSvg() {
        return this.isNewSvg || this.isLegacySvg;
      },

      svgContent() {
        if (this.isNewSvg) {
          return this.mediaObject.icon.svg_data;
        }
        if (this.isLegacySvg) {
          return this.mediaObject.svg_data;
        }
        return null;
      },

      mediaObject() {
        return this.asyncMediaObj || this.mediaObj;
      },

      aspect() {
        var aspect = {};
        if (!this.width || !this.height || !this.mediaObject || !this.mediaObject.url) {
          return aspect;
        }

        aspect.url = this.mediaObject.url;
        aspect.crop = `&fit=crop&crop=faces`;

        var watermarkParams = this.getWatermarkParams(this.mediaObject.watermark);
        if (watermarkParams) {
          aspect.crop += `&${this.getWatermarkParams(this.mediaObject.watermark)}`;
        }

        var targetRatio = this.width / this.height;
        var temp = targetRatio;
        (this.mediaObject.aspects || []).forEach(mediaAspect => {
          var ratioDifference = Math.abs(mediaAspect.m - targetRatio);
          if (ratioDifference <= temp) {
            temp = ratioDifference;
            aspect.url = mediaAspect.url || this.mediaObject.url;
            aspect.crop = `${mediaAspect.cropQuery}`;
            var watermarkParams = this.getWatermarkParams(this.mediaObject.watermark, mediaAspect);
            if (watermarkParams) {
              aspect.crop += `&${watermarkParams}`;
            }
            aspect.mediaAspect = mediaAspect;
          }
        });

        return aspect;
      },

      qualitySrcParam() {
        if (this.quality) {
          return '&q=' + this.quality;
        } else if (this.mediaObject && this.mediaObject.quality) {
          return '&q=' + this.mediaObject.quality;
        }
        return '';
      },

      src() {
        if (!this.aspect || !this.aspect.url) {
          return '';
        }
        let url = this.remapUrl(this.aspect.url);

        // replace white spaces
        url = url.replace(/ /gi, '%20');

        return `${url}?${this.aspect.crop}&auto=format${this.qualitySrcParam}`;
      },
    },

    watch: {
      mediaId: {
        handler: 'loadMediaObject',
      }
    },

    async created() {
      if (!import.meta.env.SSR) {
        await this.loadMediaObject();
      }
    },

    mounted() {
      if (this.isSvg) {
        return;
      }

      var self = this;

      self.getWidthAndHeight();
      self.interval = window.setInterval(function() {
        self.getWidthAndHeight();
      }, 300);
    },

    beforeUnmount() {
      clearInterval(this.interval);
    },

    methods: {
      getWidthAndHeight() {
        var clientWidth = this.$el.clientWidth;
        var clientHeight = this.$el.clientHeight;
        this.width = clientWidth;
        this.height = clientHeight + 1;
      },

      remapUrl(url) {
        if (!url) {
          return url;
        }

        const mediaDomain = 'https://media.madison-reed.com/';

        if (url.startsWith(mediaDomain)) {
          return url;
        }

        const imgixPrefix = 'https://mr-cdn.imgix.net/';
        const imgixPrefixStaging = 'https://mr-cdn-staging.imgix.net';
        const cloudfrontPrefix = `${mediaDomain}d3ewrnwdcmri66.cloudfront.net/`;
        const cloudfrontPrefixStaging = `${mediaDomain}ddxs94deh28np.cloudfront.net/`;

        if (url.startsWith(imgixPrefix)) {
          return cloudfrontPrefix + url.substring(imgixPrefix.length);
        }

        if (url.startsWith(imgixPrefixStaging)) {
          return cloudfrontPrefixStaging + url.substring(imgixPrefixStaging.length);
        }

        return mediaDomain + url.replace('https://', '');
      },

      getWatermarkParams(watermarkObj, mediaAspect) {
        if (!watermarkObj || !watermarkObj.url) {
          return '';
        }
        mediaAspect = mediaAspect || {};
        var markparams = [];
        var markalign = mediaAspect.markalign || watermarkObj.markalign;
        var markpad = mediaAspect.markpad || watermarkObj.markpad;
        var markw = mediaAspect.markw || watermarkObj.markw;
        var markh = mediaAspect.markh || watermarkObj.markh;
        markparams.push('mark=' + watermarkObj.url);
        markparams.push('&markalign=' + markalign);
        markparams.push('&markpad=' + markpad);
        markparams.push('&markfit=clip&markw=' + markw + '&markh=' + markh);
        return markparams.join('');
      },

      async loadMediaObject() {
        if (this.mediaId) {
          const res = await getMediaObject({
            id: this.mediaId
          });
          this.asyncMediaObj = res.data;
        }
      }
    },
  };
</script>

<style lang='stylus' scoped>
  .image-box
    width 100%
    height 100%
    overflow hidden

    img
      width 100%
      height 100%
      vertical-align middle
      object-fit cover
      object-position center
      font-size 0px

  .icon-box
    &.default-size
      width 100%!important

</style>
